// src/stores/ringcentral.ts
// RingCentral OAuth Integration Service - Pinia State Management Store
// Manages RingCentral connection state, OAuth flow, and integration status
// RELEVANT FILES: src/types/ringcentral.ts, src/api/ringcentral-api.ts, src/composables/useRingCentral.ts

import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  clearOrganizationCache,
  clearOrganizationsListCache,
  fetchRingCentralOrganization,
  fetchRingCentralOrganizations,
} from '@/api/cached-api';
import { formatRingCentralError, ringCentralAPI } from '@/api/ringcentral-api';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import type {
  CreateOrganizationRequest,
  IntegrationStatus,
  Organization,
  RingCentralAccount,
  RingCentralConnection,
  WebhookSubscription,
} from '@/types/ringcentral';

/**
 * RingCentral Integration Store
 * Handles OAuth flow, connection management, and integration state
 */
export const useRingCentralStore = defineStore('ringcentral', () => {
  // ============ Core State ============

  // Organization management
  const organization = ref<Organization | null>(null);
  const organizations = useLocalStorage<Organization[]>('ringcentral_organizations', []);

  // Connection management - keyed by orgId
  const connections = useLocalStorage<Record<string, RingCentralConnection>>(
    'ringcentral_connections',
    {},
  );

  // Current OAuth session
  const oauthState = ref<string | null>(null);
  const oauthSessionId = ref<string | null>(null);

  // Account information cache
  const accountInfoCache = ref<Record<string, RingCentralAccount>>({});

  // Webhook subscriptions cache
  const webhookSubscriptions = ref<Record<string, WebhookSubscription[]>>({});

  // UI state
  const loading = ref(false);
  const error = ref<string | null>(null);
  const lastError = ref<Error | null>(null);

  // Integration status cache
  const integrationStatusCache = ref<Record<string, IntegrationStatus>>({});

  // ============ Computed Properties ============

  /**
   * Get current organization's connection
   */
  const currentConnection = computed(() => {
    const orgStore = useOrganizationStore();
    const currentOrgId = orgStore.currentOrganization?.orgId;
    if (!currentOrgId) {return null;}
    return connections.value[currentOrgId] || null;
  });

  /**
   * Check if current organization is connected to RingCentral
   */
  const isConnected = computed(() => {
    return currentConnection.value?.status === 'connected';
  });

  /**
   * Check if current user can manage RingCentral connection
   */
  const canManageConnection = computed(() => {
    const authStore = useAuthStore();
    const orgStore = useOrganizationStore();
    const currentOrgId = orgStore.currentOrganization?.orgId;

    if (!currentOrgId || !authStore.memberships) {return false;}

    return authStore.memberships.some(
      m => m.orgId === currentOrgId && ['admin', 'owner'].includes(m.role),
    );
  });

  /**
   * Get connections for current user's organizations
   */
  const userConnections = computed(() => {
    const authStore = useAuthStore();
    if (!authStore.memberships) {return [];}

    return authStore.memberships.map(m => connections.value[m.orgId]).filter(Boolean);
  });

  /**
   * Get connection status summary
   */
  const connectionSummary = computed(() => {
    const all = userConnections.value;
    return {
      total: all.length,
      connected: all.filter(c => c.status === 'connected').length,
      pending: all.filter(c => c.status === 'pending').length,
      error: all.filter(c => c.status === 'error').length,
    };
  });

  // ============ Organization Management ============

  /**
   * Create new organization in RingCentral service
   */
  async function createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    loading.value = true;
    error.value = null;

    try {
      console.log('🏢 Creating RingCentral organization:', data.name);

      const result = await ringCentralAPI.createOrganization(data);
      organization.value = result;

      // Add new organization to the cached list
      organizations.value.push(result);

      return result;
    } catch (err) {
      const message = formatRingCentralError(err);
      error.value = message;
      lastError.value = err as Error;
      console.error('❌ Failed to create organization:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update organization details
   */
  async function updateOrganization(
    organizationId: string,
    data: Partial<CreateOrganizationRequest>,
  ): Promise<Organization> {
    loading.value = true;
    error.value = null;

    try {
      console.log('✏️ Updating RingCentral organization:', organizationId, data);

      const result = await ringCentralAPI.updateOrganization(organizationId, data);

      // Clear cache for this organization since data was updated
      clearOrganizationCache(organizationId);

      // Update organization in the cached list
      // Use splice to ensure Vue 3 reactivity triggers properly
      const index = organizations.value.findIndex(org => org.organizationId === organizationId);
      if (index !== -1) {
        organizations.value.splice(index, 1, result);
      }

      // Update current organization if it matches
      if (organization.value?.organizationId === organizationId) {
        organization.value = result;
      }

      return result;
    } catch (err) {
      const message = formatRingCentralError(err);
      error.value = message;
      lastError.value = err as Error;
      console.error('❌ Failed to update organization:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get organization details from RingCentral service (with caching)
   */
  async function getOrganization(organizationId: string, force = false): Promise<Organization> {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    loading.value = true;
    error.value = null;

    try {
      const result = await fetchRingCentralOrganization(organizationId, { force });
      organization.value = result;

      // Update organizations cache if this org exists in the list
      const existingIndex = organizations.value.findIndex(
        org => org.organizationId === organizationId,
      );
      if (existingIndex >= 0) {
        organizations.value[existingIndex] = result;
      } else {
        // Add to list if not exists
        organizations.value.push(result);
      }

      return result;
    } catch (err: any) {
      // Check if this is a 403/404 error (organization deleted or no access)
      if (
        err?.status === 403 ||
        err?.response?.status === 403 ||
        err?.status === 404 ||
        err?.response?.status === 404
      ) {
        console.warn(
          `⚠️ Organization access denied (${
            err?.status || err?.response?.status
          }), likely deleted:`,
          organizationId,
        );

        // Remove from organizations list cache
        const orgIndex = organizations.value.findIndex(org => org.organizationId === organizationId);
        if (orgIndex !== -1) {
          organizations.value.splice(orgIndex, 1);
        }

        // Clear the cache to force refresh on next fetch
        clearOrganizationCache(organizationId);
        clearOrganizationsListCache();

        // Auto-switch to first available organization if this was the current one
        const authStore = useAuthStore();
        const orgStore = useOrganizationStore();
        if (orgStore.currentOrgId === organizationId && authStore.memberships?.length > 0) {
          const firstValidOrg = authStore.memberships[0];
          console.log('🔄 Auto-switching to organization:', firstValidOrg.orgName);
          orgStore.switchOrganization(firstValidOrg.orgId);
        }
      }

      const message = formatRingCentralError(err);
      error.value = message;
      lastError.value = err as Error;
      console.error('❌ Failed to fetch organization:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get all organizations for current user from RingCentral service (with caching)
   */
  async function getUserOrganizations(force = false): Promise<Organization[]> {
    loading.value = true;
    error.value = null;

    try {
      const result = await fetchRingCentralOrganizations({ force });
      organizations.value = result.organizations;

      return result.organizations;
    } catch (err) {
      const message = formatRingCentralError(err);
      error.value = message;
      lastError.value = err as Error;
      console.error('❌ Failed to fetch user organizations:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // ============ OAuth Flow Management ============

  /**
   * Initiate OAuth authorization flow
   */
  async function initiateOAuth(
    orgId: string,
    accessLevel?: 'account' | 'extension',
  ): Promise<{ authWindow: Window; stateId: string }> {
    loading.value = true;
    error.value = null;

    try {
      console.log(
        '🔐 Initiating RingCentral OAuth for org:',
        orgId,
        'with accessLevel:',
        accessLevel || 'account (default)',
      );

      const result = await ringCentralAPI.initiateOAuth(orgId, accessLevel);
      oauthState.value = result.stateId;

      // Store stateId and orgId for tracking
      sessionStorage.setItem('ringcentral_oauth_state', result.stateId);
      sessionStorage.setItem('ringcentral_oauth_orgId', orgId);

      // Open OAuth in new window
      const authWindow = window.open(
        result.authorizationUrl,
        'ringcentral-oauth',
        'width=600,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no',
      );

      if (!authWindow) {
        throw new Error('Unable to open authorization window. Please allow popups for this site.');
      }

      return { authWindow, stateId: result.stateId };
    } catch (err) {
      const message = formatRingCentralError(err);
      error.value = message;
      lastError.value = err as Error;
      console.error('❌ OAuth initiation failed:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Check OAuth status using stateId
   */
  async function checkOAuthStatus(stateId: string): Promise<{
    status: 'PENDING' | 'SUCCESS' | 'DUPLICATE' | 'FAILED';
    errorMessage?: string;
    providerAccountId?: string;
  }> {
    try {
      console.log('🔍 Checking OAuth status for stateId:', stateId);

      const result = await ringCentralAPI.checkOAuthStatus(stateId);

      return result;
    } catch (err) {
      console.error('❌ Failed to check OAuth status:', err);
      throw err;
    }
  }

  // ============ Connection Management ============

  /**
   * Load connection information for organization
   */
  async function loadConnection(
    orgId: string,
    force = false,
  ): Promise<RingCentralConnection | null> {
    // Return cached connection if available and not forcing refresh
    if (!force && connections.value[orgId]) {
      return connections.value[orgId];
    }

    // Check if organization exists in user's memberships before making API call
    const authStore = useAuthStore();
    let orgExists = authStore.memberships?.some(m => m.orgId === orgId);

    // If not found, try refreshing memberships first (data might be stale)
    if (!orgExists) {
      console.warn('⚠️ Organization not found in cached memberships, refreshing...', orgId);
      await authStore.fetchUserMemberships(true);
      orgExists = authStore.memberships?.some(m => m.orgId === orgId);
    }

    // If still not found after refresh, clean up and switch to valid org
    if (!orgExists) {
      console.warn('⚠️ Organization not found in memberships, cleaning up:', orgId);
      // Clean up any cached connection for this deleted org
      delete connections.value[orgId];

      // Clear organization from cache
      clearOrganizationCache(orgId);

      // Auto-switch to first available organization if this was the current one
      const orgStore = useOrganizationStore();
      if (orgStore.currentOrgId === orgId && authStore.memberships?.length > 0) {
        const firstValidOrg = authStore.memberships[0];
        console.log('🔄 Auto-switching to organization:', firstValidOrg.orgName);
        orgStore.switchOrganization(firstValidOrg.orgId);
      }

      return null;
    }

    try {
      console.log('📡 Loading RingCentral connection for org:', orgId);

      // Use organization API instead of non-existent connection API
      const orgData = await ringCentralAPI.getOrganization(orgId);

      // Convert organization OAuth data to connection format
      let connection: RingCentralConnection | null = null;
      if (orgData?.oauthConnection) {
        const oauth = orgData.oauthConnection;
        connection = {
          connectionId: orgId, // Use orgId as connection identifier
          organizationId: orgId,
          userId: (oauth.userId as string) || 'unknown', // Type assertion for userId
          status: oauth.hasOAuth && oauth.status === 'ACTIVE' ? 'connected' : 'disconnected',
          connectedAt: (oauth.connectedAt as string) || orgData.createdAt,
          lastSyncAt: (oauth.lastSyncTime as string) || undefined, // Convert null to undefined
          scopes: (oauth.scopes as string[]) || [], // Type assertion for scopes array
          rcAccountId: oauth.providerAccountId as string | undefined,
          rcAccountName: oauth.accountName as string | undefined,
          syncStatus: oauth.syncStatus as any, // syncStatus is an object, not a string
        };
      }

      if (connection) {
        connections.value[orgId] = connection;
      } else {
        // Remove from cache if no connection data
        delete connections.value[orgId];
      }

      // Load additional info for connected accounts
      if (connection?.status === 'connected') {
        try {
          await loadAccountInfo(connection.connectionId);
        } catch (error) {
          console.debug('Could not load account info for connected org:', orgId, error);
        }
      }

      return connection;
    } catch (error: any) {
      // Check if this is a 403/404 error (organization deleted or no access)
      if (
        error?.status === 403 ||
        error?.response?.status === 403 ||
        error?.status === 404 ||
        error?.response?.status === 404
      ) {
        console.warn(
          `⚠️ Organization access denied (${
            error?.status || error?.response?.status
          }), likely deleted:`,
          orgId,
        );

        // Remove from cache and clean up
        delete connections.value[orgId];

        // Remove from organizations list cache
        const orgIndex = organizations.value.findIndex(org => org.organizationId === orgId);
        if (orgIndex !== -1) {
          organizations.value.splice(orgIndex, 1);
        }

        // Clear the cache to force refresh on next fetch
        clearOrganizationCache(orgId);
        clearOrganizationsListCache();

        // Auto-switch to first available organization if this was the current one
        const authStore = useAuthStore();
        const orgStore = useOrganizationStore();
        if (orgStore.currentOrgId === orgId && authStore.memberships?.length > 0) {
          const firstValidOrg = authStore.memberships[0];
          console.log('🔄 Auto-switching to organization:', firstValidOrg.orgName);
          orgStore.switchOrganization(firstValidOrg.orgId);
        }
      } else {
        console.debug('No RingCentral connection found for org:', orgId, error);
      }

      // Remove from cache on error
      delete connections.value[orgId];
      return null;
    }
  }

  /**
   * Refresh connection token
   */
  async function refreshConnection(orgId: string): Promise<RingCentralConnection> {
    const connection = connections.value[orgId];
    if (!connection) {
      throw new Error('No connection found for organization');
    }

    loading.value = true;
    error.value = null;

    try {
      const refreshed = await ringCentralAPI.refreshOAuthToken(connection.connectionId);
      connections.value[orgId] = refreshed;

      return refreshed;
    } catch (err) {
      const message = formatRingCentralError(err);
      error.value = message;
      lastError.value = err as Error;
      console.error('❌ Connection refresh failed:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Disconnect RingCentral account
   */
  async function disconnect(orgId: string): Promise<void> {
    const connection = connections.value[orgId];
    if (!connection) {
      throw new Error('No connection found for organization');
    }

    loading.value = true;
    error.value = null;

    try {
      console.log('🔌 Disconnecting RingCentral account:', connection.connectionId);

      await ringCentralAPI.revokeOAuthToken(connection.connectionId);

      // Clean up local state
      delete connections.value[orgId];
      delete accountInfoCache.value[connection.connectionId];
      delete webhookSubscriptions.value[connection.connectionId];
      delete integrationStatusCache.value[orgId];
    } catch (err) {
      const message = formatRingCentralError(err);
      error.value = message;
      lastError.value = err as Error;
      console.error('❌ Disconnect failed:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Test connection status
   */
  async function testConnection(orgId: string): Promise<IntegrationStatus> {
    const connection = connections.value[orgId];
    if (!connection) {
      throw new Error('No connection found for organization');
    }

    try {
      console.log('🧪 Testing RingCentral connection:', connection.connectionId);

      const status = await ringCentralAPI.testConnection(connection.connectionId);
      integrationStatusCache.value[orgId] = status;

      return status;
    } catch (err) {
      console.error('❌ Connection test failed:', err);
      throw err;
    }
  }

  // ============ Account Information ============

  /**
   * Load RingCentral account information
   */
  async function loadAccountInfo(connectionId: string): Promise<RingCentralAccount> {
    try {
      const accountInfo = await ringCentralAPI.getAccountInfo(connectionId);
      accountInfoCache.value[connectionId] = accountInfo;

      return accountInfo;
    } catch (err) {
      console.error('❌ Failed to load account info:', err);
      throw err;
    }
  }

  /**
   * Get cached account information
   */
  function getAccountInfo(connectionId: string): RingCentralAccount | null {
    return accountInfoCache.value[connectionId] || null;
  }

  // ============ Utilities ============

  /**
   * Clear error state
   */
  function clearError(): void {
    error.value = null;
    lastError.value = null;
  }

  /**
   * Reset all state (useful for testing/debugging)
   */
  function reset(): void {
    organization.value = null;
    connections.value = {};
    oauthState.value = null;
    oauthSessionId.value = null;
    accountInfoCache.value = {};
    webhookSubscriptions.value = {};
    integrationStatusCache.value = {};
    error.value = null;
    lastError.value = null;

    // Clear session storage
    sessionStorage.removeItem('ringcentral_oauth_state');
    sessionStorage.removeItem('ringcentral_oauth_orgId');
  }

  /**
   * Initialize store - load RingCentral organizations and connections
   */
  async function initialize(): Promise<void> {
    try {
      // Load RingCentral organizations
      await getUserOrganizations().catch(err => {
        console.log('ℹ️ Failed to load RingCentral organizations:', err.message);
      });

      // Note: We don't load connections during initialization
      // Connections will be loaded on-demand when needed
    } catch (err) {
      console.error('❌ RingCentral initialization failed:', err);
    }
  }

  // ============ Store Return ============

  return {
    // State
    organization: readonly(organization),
    organizations: readonly(organizations),
    connections: readonly(connections),
    oauthState: readonly(oauthState),
    oauthSessionId: readonly(oauthSessionId),
    loading: readonly(loading),
    error: readonly(error),
    lastError: readonly(lastError),

    // Computed
    currentConnection,
    isConnected,
    canManageConnection,
    userConnections,
    connectionSummary,

    // Organization Actions
    createOrganization,
    updateOrganization,
    getOrganization,
    getUserOrganizations,

    // OAuth Actions
    initiateOAuth,
    checkOAuthStatus,

    // Connection Actions
    loadConnection,
    refreshConnection,
    disconnect,
    testConnection,

    // Account Actions
    loadAccountInfo,
    getAccountInfo,

    // Utilities
    clearError,
    reset,
    initialize,
  };
});
